import type { Editor } from '@tiptap/react'
import type { Dispatch, MouseEvent, PointerEvent, SetStateAction } from 'react'

import {
    buildSelectionOutlineSegments,
    getCurrentTableDomInfo,
    getSelectedTableCellBounds,
    getSelectedTableCellElements,
    getTableSelectionInfo,
    getTableSelectionShape,
    getTableSelectionVariantFromShape,
    type TableSelectionAxis,
    type TableSelectionInfo,
    type TableSelectionShape,
    type TableSelectionVariant
} from './core'
import type { FloatingSurfaceState, TableContextMenuActionType, TableStructureActionType } from './TableControls.config'
import { CONTROLS_GAP, TRIGGER_THICKNESS } from './TableControls.styles'

export type OverlayState = ReturnType<typeof getCurrentTableDomInfo>

export interface SelectionCapabilities {
    showTriggers: boolean
    showInsertHotspots: boolean
    toolbarPlacement: 'top' | 'side'
    structureActions: TableStructureActionType[]
    contextMenuActions: TableContextMenuActionType[]
}

export interface TableSelectionBounds {
    top: number
    left: number
    width: number
    height: number
}

export interface ResolvedSelectionState {
    selectionInfo: TableSelectionInfo
    variant: TableSelectionVariant
    activeAxis: TableSelectionAxis | null
    shape: TableSelectionShape
    capabilities: SelectionCapabilities
    bounds: TableSelectionBounds | null
    outlineSegments: ReturnType<typeof buildSelectionOutlineSegments>
    toolbarPosition: ReturnType<typeof getFloatingToolbarPosition>
}

export function openTableContextMenu(
    setActiveSurface: Dispatch<SetStateAction<FloatingSurfaceState>>,
    payload: { x: number; y: number }
) {
    setActiveSurface({
        kind: 'contextMenu',
        x: payload.x,
        y: payload.y
    })
}

export function deriveSelectionState(options: {
    editor: Editor
    tableElement?: HTMLTableElement | null
    anchorRects?: NonNullable<OverlayState>['anchorRects'] | null
    selectionInfo?: TableSelectionInfo | null
    canMerge: boolean
    canSplit: boolean
    includeSingleCellActions: boolean
}): ResolvedSelectionState | null {
    const { editor, tableElement, anchorRects, selectionInfo, canMerge, canSplit, includeSingleCellActions } = options
    if (!anchorRects || !selectionInfo) {
        return null
    }

    const shape = getTableSelectionShape(selectionInfo)
    const capabilities = getSelectionCapabilities(shape, { canMerge, canSplit, includeSingleCellActions })
    if (!capabilities) {
        return null
    }

    const bounds = getSelectionBounds({
        editor,
        tableElement,
        anchorRects,
        selectionInfo
    })

    return {
        selectionInfo,
        variant: getTableSelectionVariantFromShape(shape),
        activeAxis: shape.isFullRowSpan ? 'row' : shape.isFullColumnSpan ? 'column' : null,
        shape,
        capabilities,
        bounds,
        outlineSegments: getSelectionOutlineSegments({
            editor,
            tableElement,
            selectionInfo
        }),
        toolbarPosition: getFloatingToolbarPosition(anchorRects, bounds, capabilities)
    }
}

function getSelectionCapabilities(
    shape: TableSelectionShape | null | undefined,
    options: { canMerge: boolean; canSplit: boolean; includeSingleCellActions: boolean }
): SelectionCapabilities | null {
    if (!shape) {
        return null
    }

    const mergeActions = options.canMerge ? (['mergeCells'] as const) : []
    const splitActions = options.canSplit ? (['splitCell'] as const) : []

    const isFullRowSpan = shape.isFullRowSpan
    const isFullColumnSpan = shape.isFullColumnSpan
    const structureActions: TableStructureActionType[] = [
        ...(isFullRowSpan ? (['deleteRow'] as const) : []),
        ...(isFullColumnSpan ? (['deleteColumn'] as const) : []),
        ...mergeActions,
        ...splitActions
    ]

    const contextMenuActions: TableContextMenuActionType[] = [
        'insertRowBefore',
        'insertRowAfter',
        'insertColumnBefore',
        'insertColumnAfter',
        ...(isFullRowSpan || (!shape.isCellRange && !shape.isSingleCell) || shape.isSingleCell ? (['deleteRow'] as const) : []),
        ...(isFullColumnSpan || (!shape.isCellRange && !shape.isSingleCell) || shape.isSingleCell ? (['deleteColumn'] as const) : []),
        ...mergeActions,
        ...splitActions,
        ...(isFullRowSpan || isFullColumnSpan || shape.isSingleCell ? (['toggleHeaderRow', 'toggleHeaderColumn'] as const) : [])
    ]

    if (shape.isCellRange && !isFullRowSpan && !isFullColumnSpan) {
        return {
            showTriggers: true,
            showInsertHotspots: true,
            toolbarPlacement: 'top',
            structureActions: [...mergeActions, ...splitActions],
            contextMenuActions: ['insertRowBefore', 'insertRowAfter', 'insertColumnBefore', 'insertColumnAfter', ...mergeActions, ...splitActions]
        }
    }

    if (shape.isSingleCell) {
        return {
            showTriggers: true,
            showInsertHotspots: true,
            toolbarPlacement: 'top',
            structureActions: options.includeSingleCellActions ? [...mergeActions, ...splitActions] : [],
            contextMenuActions
        }
    }

    return {
        showTriggers: true,
        showInsertHotspots: true,
        toolbarPlacement: isFullRowSpan ? 'side' : 'top',
        structureActions,
        contextMenuActions
    }
}

function getFloatingToolbarPosition(
    anchorRects: NonNullable<OverlayState>['anchorRects'],
    bounds?: TableSelectionBounds | null,
    capabilities?: SelectionCapabilities | null
) {
    if (!bounds || !capabilities) {
        return null
    }

    const toolbarWidth = 420
    const viewportPadding = 12

    if (capabilities.toolbarPlacement === 'top') {
        return {
            top: Math.max(viewportPadding, bounds.top - CONTROLS_GAP - TRIGGER_THICKNESS - 4),
            left: clampToViewport(bounds.left + bounds.width / 2 - toolbarWidth / 2, toolbarWidth, viewportPadding),
            transform: 'translateY(-100%)'
        }
    }

    return {
        top: clampVerticalPosition(bounds.top + bounds.height / 2 - 46, 92, viewportPadding),
        left: clampToViewport(anchorRects.tableRect.left + 8, toolbarWidth, viewportPadding)
    }
}

function getSelectionBounds(options: {
    editor: Editor
    tableElement?: HTMLTableElement | null
    anchorRects: NonNullable<OverlayState>['anchorRects']
    selectionInfo: TableSelectionInfo
}) {
    const { editor, tableElement, anchorRects, selectionInfo } = options
    const cellBounds = getSelectedTableCellBounds(editor, selectionInfo, tableElement)
    if (cellBounds) {
        return cellBounds
    }

    if (selectionInfo.colIndexes.length > 0) {
        const rowRects = selectionInfo.rowIndexes.map(index => anchorRects.rowRects[index]).filter(Boolean)
        const colRects = selectionInfo.colIndexes.map(index => anchorRects.columnRects[index]).filter(Boolean)
        const firstRowRect = rowRects[0]
        const lastRowRect = rowRects[rowRects.length - 1]
        const firstColRect = colRects[0]
        const lastColRect = colRects[colRects.length - 1]

        if (!firstRowRect || !lastRowRect || !firstColRect || !lastColRect) {
            return null
        }

        return {
            top: firstRowRect.top,
            left: firstColRect.left,
            width: lastColRect.right - firstColRect.left,
            height: lastRowRect.bottom - firstRowRect.top
        }
    }

    const rowRects = selectionInfo.rowIndexes.map(index => anchorRects.rowRects[index]).filter(Boolean)
    const firstRowRect = rowRects[0]
    const lastRowRect = rowRects[rowRects.length - 1]

    if (!firstRowRect || !lastRowRect) {
        return null
    }

    return {
        top: firstRowRect.top,
        left: anchorRects.tableRect.left,
        width: anchorRects.tableRect.width,
        height: lastRowRect.bottom - firstRowRect.top
    }
}

export function getContextMenuPosition(contextMenu: Extract<FloatingSurfaceState, { kind: 'contextMenu' }> | null) {
    if (!contextMenu) {
        return null
    }

    const menuWidth = 196
    const menuHeight = 320
    const padding = 12

    return {
        top: clampVerticalPosition(contextMenu.y + 4, menuHeight, padding),
        left: clampToViewport(contextMenu.x + 4, menuWidth, padding)
    }
}

function clampToViewport(left: number, width: number, padding: number) {
    return Math.min(Math.max(padding, left), window.innerWidth - width - padding)
}

function clampVerticalPosition(top: number, height: number, padding: number) {
    return Math.min(Math.max(padding, top), window.innerHeight - height - padding)
}

export function preventToolbarBlur(event: MouseEvent<HTMLElement> | PointerEvent<HTMLElement>) {
    event.preventDefault()
    event.stopPropagation()
}

function getSelectionOutlineSegments(options: {
    editor: Editor
    tableElement?: HTMLTableElement | null
    selectionInfo: TableSelectionInfo
}): ReturnType<typeof buildSelectionOutlineSegments> {
    const cells = getSelectedTableCellElements(options.editor, options.selectionInfo, options.tableElement)
    if (cells.length === 0) {
        return []
    }

    const rects = cells.map(cell => cell.getBoundingClientRect())
    return buildSelectionOutlineSegments(rects)
}
